/**
 * Шаг «Визуалы» (пряники; с `profile="layout"` — макеты): кабинеты плитками, план «артикул → набор → кабинеты», смета, запуск SKU-воронок
 * (по запуску на артикул × группу наборов) и таблица запусков с состоянием и превью первого слайда.
 *
 * Слайды рисует ИИ, утверждает человек на странице запуска (`/content-funnel-series/:runId`, «Утвердить и
 * опубликовать»): здесь только состояние и ссылка. Право — `ContentFunnel:UPDATE`.
 *
 * Профиль (`profile`) выбирает наборы слайдов и запуски: `gingerbread` по умолчанию (поведение пряников прежнее),
 * `layout` — наборы макетов; артикулы и их категории тогда берутся из каталога товаров.
 */

import { useMemo, useState } from 'react'
import { AlertTriangle, ExternalLink, Image as ImageIcon, Images, Loader2, RefreshCw } from 'lucide-react'
import { Badge, Button, ConfirmDialog } from '../../../components/ui'
import { SellerTargetTiles } from '../../../components/SellerTargetTiles'
import { AuthorizedMediaImage } from '../../../shared/media/authorizedMedia'
import { useToastStore } from '../../../stores/toastStore'
import { runStateMeta, seriesRunPath, usd } from '../../content-funnel-series/labels'
import type { RunSummary } from '../../content-funnel-series/types'
import { gingerbreadVisualsFlowAPI } from './api'
import { useArticleMeta, useSkuRuns, useVisualsPlan, type TargetSelection } from './hooks'
import {
  articlesWithoutSets,
  articlesWithRuns,
  chunk,
  DEFAULT_VISUALS_PROFILE,
  marketplaceShort,
  MAX_ARTICLES_PER_CALL,
  plannedRunCount,
  sellerLabel,
  skuTargetOf,
  summarizeLaunch,
  type LaunchSummary,
} from './model'
import type { SkuBatchItem, VisualsProfile } from './types'

export interface VisualsStepProps {
  articles: string[]
  selection: TargetSelection
  /** Право `ContentFunnel:UPDATE`: без него запуск закрыт, таблица запусков остаётся. */
  canRun: boolean
  /** Профиль наборов слайдов; по умолчанию пряники. */
  profile?: VisualsProfile
}

const errorText = (error: unknown, fallback: string): string => {
  const value = error as { response?: { data?: { error?: { message?: string } } }; message?: string } | null
  return value?.response?.data?.error?.message || value?.message || fallback
}

const sectionTitle = 'mb-2 text-2xs font-semibold uppercase tracking-wider text-gray-500 dark:text-zinc-400'

export function VisualsStep({ articles, selection, canRun, profile = DEFAULT_VISUALS_PROFILE }: VisualsStepProps) {
  const showToast = useToastStore((state) => state.showToast)
  const { sellers, selected, selectedSellers, toggle } = selection
  const isLayout = profile === 'layout'
  const { meta, loading: metaLoading } = useArticleMeta(articles, sellers.loadState === 'ready', profile)
  const planState = useVisualsPlan(articles, meta, metaLoading, selectedSellers, sellers.loadState === 'ready', profile)
  const runsState = useSkuRuns(articles, true, profile)

  const [launching, setLaunching] = useState(false)
  const [confirmRelaunch, setConfirmRelaunch] = useState(false)
  const [summary, setSummary] = useState<LaunchSummary | null>(null)

  const skuTargets = useMemo(
    () => selectedSellers.map(skuTargetOf).filter((target): target is NonNullable<typeof target> => target !== null),
    [selectedSellers],
  )
  const sellersWithoutCode = selectedSellers.filter((seller) => skuTargetOf(seller) === null)
  const withoutSets = articlesWithoutSets(planState.plan)
  const everythingWithoutSets = planState.state === 'ready' && articles.length > 0 && withoutSets.length === articles.length
  const existing = articlesWithRuns(runsState.runs).filter((article) => articles.includes(article))

  const runIssue: string | null = !canRun
    ? 'Нужно право «Воронки контента: изменение» (ContentFunnel:UPDATE)'
    : sellers.loadState !== 'ready'
      ? 'Кабинеты ещё не загружены'
      : skuTargets.length === 0
        ? 'Выберите хотя бы один кабинет с машинным кодом'
        : planState.state === 'loading' || metaLoading
          ? 'Подбираем наборы слайдов'
          : everythingWithoutSets
            ? 'Для выбранных кабинетов нет набора слайдов: визуалов не будет'
            : null

  const launch = async () => {
    setConfirmRelaunch(false)
    if (runIssue || launching) return
    setLaunching(true)
    setSummary(null)
    const collected: SkuBatchItem[] = []
    try {
      for (const part of chunk(articles, MAX_ARTICLES_PER_CALL)) {
        collected.push(...(await (isLayout
          ? gingerbreadVisualsFlowAPI.createSkuRuns(part, skuTargets, profile)
          : gingerbreadVisualsFlowAPI.createSkuRuns(part, skuTargets))))
      }
      const result = summarizeLaunch(collected)
      setSummary(result)
      showToast(
        result.created > 0
          ? `Запущено генерацию визуалов: ${result.created.toLocaleString('ru-RU')} запусков`
          : 'Ни один запуск не создан: смотрите причины ниже',
        result.created > 0 ? 'success' : 'warning',
      )
    } catch (error) {
      // Часть пакетов могла уйти до сбоя: показываем, что успело создаться.
      if (collected.length > 0) setSummary(summarizeLaunch(collected))
      showToast(`Не удалось запустить генерацию: ${errorText(error, 'ошибка')}`, 'error')
    } finally {
      setLaunching(false)
      void runsState.refresh()
    }
  }

  const onLaunchClick = () => {
    if (runIssue) return
    if (existing.length > 0) {
      setConfirmRelaunch(true)
      return
    }
    void launch()
  }

  const estimate = planState.estimate
  const totalLabel = estimate && estimate.usd != null ? usd(estimate.usd, estimate.currency) : null

  return (
    <div className="space-y-5" data-testid="gingerbread-visuals-step">
      <div>
        <h3 className="text-base font-semibold text-gray-900 dark:text-zinc-100">Визуалы</h3>
        <p className="mt-1 text-sm text-gray-500 dark:text-zinc-400">
          Слайды карточек рисует ИИ: по одному запуску на артикул и группу кабинетов с одинаковым набором слайдов.
          {isLayout
            ? 'Утверждает слайды человек на странице запуска — после публикации визуалы лягут в «Медиа» товара по кабинетам.'
            : 'Утверждает слайды человек на странице запуска — после публикации можно создавать карточки.'}
        </p>
      </div>

      <div>
        <h4 className={sectionTitle}>Кабинеты</h4>
        <SellerTargetTiles
          targets={sellers.targets}
          loadState={sellers.loadState}
          loadError={sellers.loadError}
          unsupportedCount={sellers.unsupportedCount}
          selected={selected}
          onToggle={toggle}
          onRetry={() => void sellers.load()}
          emptySelectionHint="Выберите хотя бы один кабинет."
        />
        {sellersWithoutCode.length > 0 && (
          <p role="note" className="mt-2 text-xs text-amber-700 dark:text-amber-300">
            У кабинетов без машинного кода визуалы не запускаются: {sellersWithoutCode.map((seller) => seller.name).join(', ')}.
          </p>
        )}
      </div>

      <div>
        <h4 className={sectionTitle}>План: артикул, набор слайдов, кабинеты</h4>
        {planState.state === 'loading' || metaLoading ? (
          <p role="status" className="flex items-center gap-2 text-xs text-gray-500 dark:text-zinc-400">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            Подбираем наборы слайдов…
          </p>
        ) : planState.state === 'error' ? (
          <p role="alert" className="text-xs text-red-600 dark:text-red-400">{planState.error}</p>
        ) : planState.plan.length === 0 ? (
          <p className="text-xs text-gray-500 dark:text-zinc-400">
            {skuTargets.length === 0 ? 'Выберите кабинеты, чтобы увидеть план.' : 'Состав наборов определится при запуске.'}
          </p>
        ) : (
          <div className="max-h-64 overflow-auto rounded-lg border border-gray-200 dark:border-zinc-800">
            <table className="w-full text-left text-sm" aria-label="План визуалов по артикулам">
              <thead className="sticky top-0 bg-gray-50 text-xs text-gray-500 dark:bg-zinc-900 dark:text-zinc-400">
                <tr>
                  <th scope="col" className="px-3 py-2 font-medium">Артикул</th>
                  <th scope="col" className="px-3 py-2 font-medium">Набор слайдов и кабинеты</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-zinc-800">
                {planState.plan.map((item) => (
                  <tr key={item.article} className="align-top">
                    <td className="whitespace-nowrap px-3 py-2">
                      <span className="font-mono text-sm text-gray-900 dark:text-zinc-100">{item.article}</span>
                      {item.title && <span className="block max-w-[14rem] truncate text-xs text-gray-500 dark:text-zinc-400" title={item.title}>{item.title}</span>}
                    </td>
                    <td className="px-3 py-2">
                      {!item.known ? (
                        <span className="text-xs text-gray-500 dark:text-zinc-400">Категория не определена: набор подберётся при запуске</span>
                      ) : (
                        <ul className="space-y-1">
                          {item.groups.map((group) => (
                            <li key={group.setIds.join(',')} className="text-xs text-gray-700 dark:text-zinc-300">
                              <span className="font-medium">{group.setNames.join(' + ')}</span>
                              <span className="text-gray-500 dark:text-zinc-400"> → {group.sellers.map(sellerLabel).join(', ')}</span>
                            </li>
                          ))}
                          {item.uncovered.length > 0 && (
                            <li className="flex items-start gap-1.5 text-xs text-amber-700 dark:text-amber-300">
                              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                              <span>
                                Нет набора — визуалов и карточки не будет: {item.uncovered.map(sellerLabel).join(', ')}
                              </span>
                            </li>
                          )}
                        </ul>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Button
          variant="primary"
          icon={<Images className="h-4 w-4" />}
          loading={launching}
          disabled={Boolean(runIssue)}
          title={runIssue ?? undefined}
          onClick={onLaunchClick}
        >
          Сгенерировать визуалы
        </Button>
        {planState.plan.length > 0 && (
          <p className="text-xs text-gray-600 dark:text-zinc-300" data-testid="visuals-estimate">
            Запусков: {plannedRunCount(planState.plan).toLocaleString('ru-RU')}
            {totalLabel ? <>, ориентировочно {totalLabel}{estimate?.partial ? ' (без части запусков)' : ''}</> : ', стоимость уточняется'}
          </p>
        )}
        {runIssue && !launching && (
          <p role="note" className="text-xs text-amber-700 dark:text-amber-300">{runIssue}</p>
        )}
      </div>

      {summary && (
        <div role="status" className="space-y-2 rounded-lg border border-gray-200 bg-gray-50 p-3 text-xs text-gray-700 dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-300">
          <p className="font-medium">
            Создано запусков: {summary.created.toLocaleString('ru-RU')} по артикулам: {summary.articlesLaunched.toLocaleString('ru-RU')}
          </p>
          {summary.excluded.length > 0 && (
            <ul className="space-y-1" aria-label="Кабинеты без набора слайдов">
              {summary.excluded.map((entry) => (
                <li key={`${entry.marketplace}:${entry.cabinet}:${entry.message}`} className="flex items-start gap-1.5 text-amber-700 dark:text-amber-300">
                  <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                  <span>
                    {marketplaceShort(entry.marketplace)}{entry.cabinet ? ` · ${entry.cabinet}` : ''} исключён у артикулов: {entry.articles}. {entry.message}
                  </span>
                </li>
              ))}
            </ul>
          )}
          {summary.errors.length > 0 && (
            <ul className="space-y-1 text-red-700 dark:text-red-300" aria-label="Артикулы без запуска">
              {summary.errors.map((entry, index) => (
                <li key={`${entry.article}-${index}`}>
                  <span className="font-mono">{entry.article}</span>: {entry.message}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div>
        <div className="mb-2 flex items-center justify-between gap-2">
          <h4 className={`${sectionTitle} mb-0`}>Запуски</h4>
          <Button size="sm" variant="secondary" icon={<RefreshCw className="h-3.5 w-3.5" />} onClick={() => void runsState.refresh()}>
            Обновить
          </Button>
        </div>
        {runsState.error ? (
          <p role="alert" className="text-xs text-red-600 dark:text-red-400">{runsState.error}</p>
        ) : runsState.loading && runsState.runs.length === 0 ? (
          <p role="status" className="flex items-center gap-2 text-xs text-gray-500 dark:text-zinc-400">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            Загружаем запуски…
          </p>
        ) : (
          <SkuRunsTable runs={runsState.runs} />
        )}
        <p className="mt-2 text-xs text-gray-500 dark:text-zinc-400">
          {isLayout
            ? 'Утвердите визуалы на странице запуска — после публикации они лягут в «Медиа» товара по кабинетам.'
            : 'Утвердите визуалы на странице запуска — после публикации можно создавать карточки.'}
        </p>
      </div>

      <ConfirmDialog
        isOpen={confirmRelaunch}
        title="Запустить визуалы повторно?"
        message={`У артикулов уже есть запуски: ${existing.length.toLocaleString('ru-RU')}. Новые запуски платные и появятся рядом с прежними.`}
        confirmLabel="Запустить"
        cancelLabel="Отмена"
        onConfirm={() => void launch()}
        onClose={() => setConfirmRelaunch(false)}
      />
    </div>
  )
}

/** Таблица SKU-запусков: превью первого слайда, артикул, набор, кабинеты, состояние, «Открыть» в новой вкладке. */
export function SkuRunsTable({ runs }: { runs: RunSummary[] }) {
  if (runs.length === 0) {
    return <p className="text-xs text-gray-500 dark:text-zinc-400">Запусков визуалов пока нет.</p>
  }
  return (
    <div className="max-h-72 overflow-auto rounded-lg border border-gray-200 dark:border-zinc-800">
      <table className="w-full text-left text-sm" aria-label="Запуски визуалов по артикулам">
        <thead className="sticky top-0 bg-gray-50 text-xs text-gray-500 dark:bg-zinc-900 dark:text-zinc-400">
          <tr>
            <th scope="col" className="px-3 py-2 font-medium">Слайд</th>
            <th scope="col" className="px-3 py-2 font-medium">Артикул</th>
            <th scope="col" className="px-3 py-2 font-medium">Набор и кабинеты</th>
            <th scope="col" className="px-3 py-2 font-medium">Состояние</th>
            <th scope="col" className="px-3 py-2 font-medium"><span className="sr-only">Действие</span></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 dark:divide-zinc-800">
          {runs.map((run) => {
            const meta = runStateMeta(run.state)
            const cabinets = (run.targets ?? []).map((target) => `${marketplaceShort(target.marketplace)} · ${target.cabinet}`).join(', ')
            const sets = (run.slideSetNames ?? []).join(', ')
            return (
              <tr key={run.id} className="align-top">
                <td className="px-3 py-2">
                  <span className="block aspect-[3/4] w-10 overflow-hidden rounded border border-gray-200 bg-gray-100 dark:border-zinc-700 dark:bg-zinc-950">
                    {run.firstSlideUrl ? (
                      <AuthorizedMediaImage src={run.firstSlideUrl} alt={`Первый слайд ${run.article ?? run.name}`} className="h-full w-full object-cover" />
                    ) : (
                      <span className="flex h-full w-full items-center justify-center text-gray-400 dark:text-zinc-500">
                        <ImageIcon className="h-4 w-4" aria-hidden="true" />
                      </span>
                    )}
                  </span>
                </td>
                <td className="whitespace-nowrap px-3 py-2 font-mono text-sm text-gray-900 dark:text-zinc-100">{run.article ?? '—'}</td>
                <td className="px-3 py-2 text-xs text-gray-700 dark:text-zinc-300">
                  <span className="block font-medium">{sets || '—'}</span>
                  <span className="block text-gray-500 dark:text-zinc-400">{cabinets || '—'}</span>
                </td>
                <td className="px-3 py-2">
                  <Badge variant={meta.variant} dot>{meta.label}</Badge>
                  {run.error?.message && run.state === 'failed' && (
                    <span className="mt-1 block max-w-[16rem] text-2xs text-red-600 dark:text-red-400">{run.error.message}</span>
                  )}
                </td>
                <td className="whitespace-nowrap px-3 py-2 text-right">
                  <a
                    href={seriesRunPath(run.id)}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Страница запуска откроется в новой вкладке: там утверждение и публикация"
                    className="inline-flex items-center gap-1 text-xs text-indigo-600 hover:underline dark:text-indigo-400"
                  >
                    <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
                    Открыть
                  </a>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
